import { AlertTriangle, CheckCircle2, Clock, type LucideIcon, XCircle } from "lucide-react";

import { ColocaButton } from "@/shared/components/buttons/ColocaButton";
import type { VerificationTranslations } from "@/shared/lang/interfaces/verification";
import useTranslations from "@/shared/lang/useTranslations";
import { cn } from "@/shared/lib/utils";

import { resolveRejectionDescription } from "../constants/verification.constants";
import { useAutoAdvance } from "../hooks/useAutoAdvance";
import type { VerificationResult } from "../types/verification.types";

export interface ResultScreenProps {
  result: VerificationResult;
  /** Backend rejection reason i18n key — only used for the `rejected` variant. */
  rejectionReason?: string;
  primaryLoading?: boolean;
  onPrimary?: () => void;
  /**
   * Overrides the result's own description. The reader who arrived through a shared link and has no account
   * here is told what they achieved, not to "continue" somewhere they cannot reach.
   */
  descriptionKey?: keyof VerificationTranslations;
  /**
   * Muted closing line under the description. This is what replaces the CTA when there is nowhere to go: the
   * dead end is closed with a sentence, not with a button that would lie about having a destination.
   */
  noteKey?: keyof VerificationTranslations;
  /** Overrides the primary action's label — the embedded frame closes its dialog rather than navigating. */
  primaryLabelKey?: keyof VerificationTranslations;
  /**
   * Seconds before `onPrimary` fires on its own. Omit, or pass `null`, to leave the screen still — which is
   * the default: only a verdict that landed while the reader was watching earns an auto-advance.
   */
  autoAdvanceSeconds?: number | null;
}

interface ResultMeta {
  icon: LucideIcon;
  tone: string;
  titleKey: keyof VerificationTranslations;
  descriptionKey: keyof VerificationTranslations;
  ctaKey: keyof VerificationTranslations;
}

const RESULT_META: Record<VerificationResult, ResultMeta> = {
  verified: {
    icon: CheckCircle2,
    tone: "bg-success/10 text-success",
    titleKey: "result_verified_title",
    descriptionKey: "result_verified_description",
    ctaKey: "result_verified_cta",
  },
  review: {
    icon: Clock,
    tone: "bg-blue-500/10 text-blue-700 dark:bg-blue-400/20 dark:text-blue-300",
    titleKey: "result_review_title",
    descriptionKey: "result_review_description",
    ctaKey: "result_review_cta",
  },
  rejected: {
    icon: XCircle,
    tone: "bg-destructive/10 text-destructive",
    titleKey: "result_rejected_title",
    descriptionKey: "result_rejected_description",
    ctaKey: "result_rejected_cta",
  },
  expired: {
    icon: AlertTriangle,
    tone: "bg-muted text-muted-foreground",
    titleKey: "result_expired_title",
    descriptionKey: "result_expired_description",
    ctaKey: "result_expired_cta",
  },
};

/** Terminal result screen: verified / review / rejected (with reason) / expired. */
export function ResultScreen({
  result,
  rejectionReason,
  primaryLoading,
  onPrimary,
  descriptionKey,
  noteKey,
  primaryLabelKey,
  autoAdvanceSeconds = null,
}: ResultScreenProps) {
  const { t } = useTranslations("verification");
  const meta = RESULT_META[result];
  const Icon = meta.icon;

  // The countdown fires the SAME action as the button, so the two can never take the reader to different
  // places; and with no action to fire there is nothing to count down to.
  const { remaining, cancel } = useAutoAdvance(onPrimary ? autoAdvanceSeconds : null, () => onPrimary?.());

  const description =
    result === "rejected"
      ? resolveRejectionDescription(rejectionReason, t)
      : t(descriptionKey ?? meta.descriptionKey);

  return (
    <div className="space-y-6 text-center" aria-live="polite">
      <span className={cn("mx-auto flex size-14 items-center justify-center rounded-full", meta.tone)}>
        <Icon className="size-7" aria-hidden="true" />
      </span>
      <div className="space-y-2">
        <h1 className="text-2xl font-semibold text-foreground">{t(meta.titleKey)}</h1>
        <p className="text-sm text-muted-foreground">{description}</p>
        {noteKey ? <p className="text-sm text-muted-foreground">{t(noteKey)}</p> : null}
      </div>
      {onPrimary ? (
        <div className="space-y-3">
          <ColocaButton variant="primary" className="w-full" loading={primaryLoading} onClick={onPrimary}>
            {t(primaryLabelKey ?? meta.ctaKey)}
          </ColocaButton>
          {remaining === null ? null : (
            <div className="space-y-1">
              {/* Announced once, in words. The ticking number below is hidden from assistive tech, or the
                  `aria-live` region wrapping this screen would read a new second out loud every second. */}
              <span className="sr-only">{t("result_verified_redirect_announce")}</span>
              <p className="text-xs text-muted-foreground" aria-hidden="true">
                {t("result_verified_redirect_hint", { seconds: remaining })}
              </p>
              <ColocaButton variant="ghost" size="sm" className="w-full" onClick={cancel}>
                {t("result_verified_redirect_cancel")}
              </ColocaButton>
            </div>
          )}
        </div>
      ) : null}
    </div>
  );
}
